import React, {useEffect, useState} from 'react';
import './SnowEffect.css';
import MissMinutesTimely from "./MissMinutesTimely";

interface Snowflake {
  id : number;
  x : number;
  y : number;
  size : number;
  speed : number;
  rotation : number;
  rotationSpeed : number;
  windSpeed : number;
  column : number;
  delay : number;
}

type Props = {
  currentTimelyStreak : number;
}

const SnowEffect = ({currentTimelyStreak} : Props) => {
    const [snowflakes, setSnowflakes] = useState<Snowflake[]>([]);

    useEffect(() => {
        const COLUMNS = 12; // Fixed number of columns across screen
        const COLUMN_WIDTH = window.innerWidth / COLUMNS;
        const SPAWN_INTERVAL = 1600; // ms between spawns in each column (reduced for more snow)

        const createSnowflake = (column : number, delay  = 0) : Snowflake => ({
            id: Math.random(),
            x: column * COLUMN_WIDTH + (COLUMN_WIDTH / 2) + (Math.random() - 0.5) * 40, // Center in column with slight variation
            y: -100 - delay * 0.5, // Stagger vertically based on delay
            size: Math.random() * 25 + 25, // 25-50px
            speed: Math.random() * 2 + 2.5, // 2.5-4.5px per frame
            rotation: Math.random() * 360,
            rotationSpeed: (Math.random() - 0.5) * 3,
            windSpeed: (Math.random() - 0.5) * 2, // -1 to 1px horizontal movement (bidirectional)
            column,
            delay,
        });

        const maxHeight = window.innerWidth > 600 ? 300 : window.innerHeight;
        const updateSnowflakes = () => {
            setSnowflakes(prev => {
                return prev.map(flake => ({
                    ...flake,
                    y: flake.y + flake.speed,
                    x: Math.max(-50, Math.min(window.innerWidth + 50, flake.x + flake.windSpeed)), // Keep within bounds with buffer
                    rotation: flake.rotation + flake.rotationSpeed,
                })).filter(flake => flake.y < maxHeight);
            });
        };

        // Create initial snowflakes for seamless coverage
        const initialFlakes : Snowflake[] = [];
        const ROWS_TO_FILL = Math.ceil(maxHeight / 50); // Even more rows, tighter spacing

        for (let column = 0; column < COLUMNS; column++) {
            for (let row = 0; row < ROWS_TO_FILL; row++) {
                const delay = (row * SPAWN_INTERVAL / 6) + (column * SPAWN_INTERVAL / COLUMNS);
                const flake = createSnowflake(column, delay);
                flake.y = row * 50 - 100 + (Math.random() * 25); // Even tighter vertical distribution
                initialFlakes.push(flake);
            }
        }

        setSnowflakes(initialFlakes);

        // Continuous spawning system for seamless effect
        const spawnIntervals : Array<ReturnType<typeof setInterval>> = [];

        for (let column = 0; column < COLUMNS; column++) {
            const interval = setInterval(() => {
                setSnowflakes(prev => {
                    // Allow more snowflakes per column for denser effect
                    const recentInColumn = prev.filter(f =>
                        f.column === column && f.y < 150
                    ).length;

                    if (recentInColumn < 5) { // Allow up to 5 recent snowflakes per column
                        return [...prev, createSnowflake(column)];
                    }
                    return prev;
                });
            }, SPAWN_INTERVAL + (column * 30)); // Even less stagger for more consistent spawning

            spawnIntervals.push(interval);
        }

        const updateInterval = setInterval(updateSnowflakes, 35);

        return () => {
            clearInterval(updateInterval);
            spawnIntervals.forEach(interval => clearInterval(interval));
        };
    }, []);

    return (
        <div className="snow-container">
            {snowflakes.map(flake => (
                <div
                    key={flake.id}
                    className="snowflake"
                    style={{
                        left: `${flake.x}px`,
                        top: `${flake.y}px`,
                        width: `${flake.size}px`,
                        height: `${flake.size}px`,
                        transform: `rotate(${flake.rotation}deg)`,
                    }}
                >
                    <MissMinutesTimely currentTimelyStreak={currentTimelyStreak} height={flake.size}/>
                </div>
            ))}
        </div>
    );
}

export default SnowEffect;
